import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, Link, Sheet, Table, Typography } from '@mui/joy';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { formatRemaining } from './telemetry/UploadJobStatusChip';
import { formatBytes } from './dashboard/DashboardWidgets';

export const FOOTAGE_SHARE_STATUSES = [
    'requested', 'uploading', 'completed', 'partial', 'failed', 'cancelled', 'deleted',
];

const STATUS_COLORS = {
    requested: 'neutral',
    uploading: 'primary',
    completed: 'success',
    partial: 'warning',
    failed: 'danger',
    cancelled: 'neutral',
    deleted: 'neutral',
};

const SLICE_STATUS_COLORS = { draft: 'neutral', frozen: 'success', failed: 'danger' };

const formatTime = (ms) => (ms != null ? new Date(ms).toLocaleString() : '—');

const formatWindow = (fromMs, toMs) => {
    if (fromMs == null || toMs == null) return '—';
    return `${formatTime(fromMs)} → ${formatTime(toMs)} (${formatRemaining(toMs - fromMs)})`;
};

/** "Visit ends after 15 min (processed)", plus a note when it changed mid-window. */
export const formatAiSetting = (used) => {
    if (!used) return '—';
    const ms = used.session_fade_ms;
    if (used.source === 'unknown' || ms == null) return 'Unknown';
    const minutes = ms / 60000;
    const value = minutes >= 1
        ? `${Number.isInteger(minutes) ? minutes : minutes.toFixed(1)} min`
        : `${Math.round(ms / 1000)} s`;
    const changed = used.changed_in_window ? ' · changed in window' : '';
    return `Visit ends after ${value} (${used.source || 'unknown'})${changed}`;
};

const formatProgress = (share) => {
    const expected = share.expected_recordings != null ? share.expected_recordings : '?';
    const expectedBytes = share.expected_bytes != null ? formatBytes(share.expected_bytes) : '?';
    const missing = share.missing_recordings > 0 ? ` · ${share.missing_recordings} missing` : '';
    return `${share.uploaded_recordings ?? 0}/${expected} rec · ${formatBytes(share.uploaded_bytes ?? 0)}/${expectedBytes}${missing}`;
};

const Detail = ({ label, children }) => (
    <Box>
        <Typography level="body-xs" textColor="neutral.500">{label}</Typography>
        <Typography level="body-sm">{children}</Typography>
    </Box>
);

/** Slices and the fields that don't fit the row, shown under a clicked share. */
const ShareDetails = ({ share }) => {
    const datasets = Array.isArray(share.datasets) ? share.datasets : [];
    const used = share.ai_settings_used;
    return (
        <Box sx={{ p: 2, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr 1fr', alignContent: 'start' }}>
                <Detail label="Effective window">
                    {formatWindow(share.effective_from_ms, share.effective_to_ms)}
                </Detail>
                <Detail label="Cameras">
                    {(share.camera_ids || []).join(', ') || '—'}
                </Detail>
                <Detail label="Consent">
                    {share.consent_version || '—'} · {formatTime(share.consented_at)}
                </Detail>
                <Detail label="Created / Updated">
                    {formatTime(share.created_at)} / {formatTime(share.updated_at)}
                </Detail>
                <Detail label="AI setting used">
                    {formatAiSetting(used)}
                    {used?.committed_watermark_ms != null && ` · processed up to ${formatTime(used.committed_watermark_ms)}`}
                </Detail>
                {share.note && <Detail label="Note">{share.note}</Detail>}
                {share.error && (
                    <Detail label="Error">
                        <Typography level="body-sm" color="danger">{share.error}</Typography>
                    </Detail>
                )}
            </Box>
            <Box>
                <Typography level="body-xs" textColor="neutral.500" sx={{ mb: 0.5 }}>
                    Slices ({datasets.length})
                </Typography>
                {datasets.length === 0 ? (
                    <Typography level="body-sm" textColor="neutral.500">Not planned by the hub yet</Typography>
                ) : (
                    <Table size="sm" variant="outlined" sx={{ '--TableCell-paddingY': '4px' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 50 }}>#</th>
                                <th style={{ width: 110 }}>Dataset</th>
                                <th>Window</th>
                                <th style={{ width: 90 }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {datasets.map((d) => (
                                <tr key={d.slice_index}>
                                    <td>{d.slice_index}</td>
                                    <td>{d.dataset_id != null ? d.dataset_id : '—'}</td>
                                    <td>{formatWindow(d.from_ms, d.to_ms)}</td>
                                    <td>
                                        <Chip size="sm" variant="soft" color={SLICE_STATUS_COLORS[d.status] || 'neutral'}>
                                            {d.status}
                                        </Chip>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Box>
        </Box>
    );
};

const Cell = ({ children, color }) => (
    <td>
        <Typography level="body-sm" color={color}>{children}</Typography>
    </td>
);

/**
 * Footage shares table shared by the admin page and the hub details section.
 * Clicking a row expands its slices; showHub hides the hub column on the hub page.
 */
const FootageSharesTable = ({ items, showHub = true }) => {
    const [expandedId, setExpandedId] = useState(null);
    const columns = 12 + (showHub ? 1 : 0);

    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
            <Box sx={{ overflow: 'auto' }}>
                <Table hoverRow size="sm" sx={{ minWidth: 1500 }}>
                    <thead>
                        <tr>
                            <th style={{ width: 70 }}>ID</th>
                            {showHub && <th style={{ width: 70 }}>Hub</th>}
                            <th style={{ width: 70 }}>Client</th>
                            <th style={{ width: 80 }}>Mode</th>
                            <th style={{ width: 110 }}>Status</th>
                            <th style={{ width: 300 }}>Requested window</th>
                            <th style={{ width: 80 }}>Cameras</th>
                            <th style={{ width: 220 }}>Progress</th>
                            <th style={{ width: 180 }}>Started / Completed</th>
                            <th style={{ width: 220 }}>AI setting used</th>
                            <th>Note</th>
                            <th>Error</th>
                            <th style={{ width: 40 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((share) => {
                            const expanded = expandedId === share.id;
                            return (
                                <React.Fragment key={share.id}>
                                    <tr
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setExpandedId(expanded ? null : share.id)}
                                    >
                                        <Cell>{share.id}</Cell>
                                        {showHub && (
                                            <td>
                                                <Link
                                                    component={RouterLink}
                                                    to={`/hub/${share.hub_id}`}
                                                    level="body-sm"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {share.hub_id}
                                                </Link>
                                            </td>
                                        )}
                                        <td>
                                            <Link
                                                component={RouterLink}
                                                to={`/client/${share.client_id}`}
                                                level="body-sm"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {share.client_id}
                                            </Link>
                                        </td>
                                        <Cell>{share.mode}</Cell>
                                        <td>
                                            <Chip size="sm" variant="soft" color={STATUS_COLORS[share.status] || 'neutral'}>
                                                {share.status}
                                            </Chip>
                                        </td>
                                        <Cell>{formatWindow(share.requested_from_ms, share.requested_to_ms)}</Cell>
                                        <Cell>{(share.camera_ids || []).length}</Cell>
                                        <Cell>{formatProgress(share)}</Cell>
                                        <Cell>
                                            {formatTime(share.started_at)}
                                            <br />
                                            {formatTime(share.completed_at)}
                                        </Cell>
                                        <Cell>{formatAiSetting(share.ai_settings_used)}</Cell>
                                        <Cell>{share.note || '—'}</Cell>
                                        <Cell color={share.error ? 'danger' : undefined}>{share.error || '—'}</Cell>
                                        <td>
                                            {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                                        </td>
                                    </tr>
                                    {expanded && (
                                        <tr>
                                            <td colSpan={columns} style={{ padding: 0, backgroundColor: 'var(--joy-palette-background-level1)' }}>
                                                <ShareDetails share={share} />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </Table>
            </Box>
        </Sheet>
    );
};

export default FootageSharesTable;
